/**
 * Upload manual de BHE. Basta con el PDF: el XML es opcional.
 */
import { useState } from 'react'
import { Upload } from 'lucide-react'

import { Button } from '@/components/Button'
import { useToast } from '@/components/Toast'
import { useUploadHonorarios } from '../hooks/useHonorarios'
import { Modal } from '@/components/ds/Modal'


export function HonorariosUploadModal({ onClose, onUploaded }: {
  onClose: () => void
  onUploaded: (id: string) => void
}) {
  const [xml, setXml] = useState<File | null>(null)
  const [pdf, setPdf] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const upload = useUploadHonorarios()
  const toast = useToast()

  const submit = async () => {
    setError(null)
    if (!xml && !pdf) {
      setError('Adjuntar al menos uno de los dos archivos.')
      return
    }
    try {
      const bill = await upload.mutateAsync({ xml, pdf })
      toast.success('BHE cargada y parseada.')
      onUploaded(bill.id)
    } catch (exc: any) {
      setError(exc?.response?.data?.error?.message || 'Error al subir la BHE.')
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Subir BHE"
      dismissOnScrimClick={false}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} leftIcon={<Upload size={14} />} isPending={upload.isPending} pendingLabel="Subiendo…">
            Subir BHE
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-content-muted">
          Los datos se leen del archivo y quedan para revisar antes de
          contabilizar. Basta con uno de los dos: con solo el PDF alcanza.
        </p>

        <FileInput label="Archivo PDF" accept=".pdf,application/pdf"
          value={pdf} onChange={setPdf} />
        <FileInput label="Archivo XML (opcional)" accept=".xml,application/xml,text/xml"
          value={xml} onChange={setXml} />

        {pdf && !xml && (
          <p className="text-xs text-content-subtle">
            El XML es el documento tributario. Se puede cargar después: la
            boleta se completa sola, sin duplicarse.
          </p>
        )}

        {error && (
          <div className="text-xs text-danger-content bg-danger-surface border border-danger-border rounded-lg p-2">
            {error}
          </div>
        )}
      </div>
    </Modal>
  )
}


function FileInput({ label, accept, value, onChange }: {
  label: string
  accept: string
  value: File | null
  onChange: (f: File | null) => void
}) {
  return (
    <div>
      <label className="text-xs font-medium text-content-muted block mb-1">{label}</label>
      <input aria-label={label}
        type="file"
        accept={accept}
        onChange={e => onChange(e.target.files?.[0] ?? null)}
        className="block w-full text-xs text-content-muted
                   file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0
                   file:text-xs file:font-medium
                   file:bg-primary-50 file:text-primary-700
                   hover:file:bg-primary-100 file:cursor-pointer"
      />
      {value && (
        <p className="mt-1 text-xs text-content-subtle truncate">
          {value.name} — {Math.round(value.size / 1024)} KB
        </p>
      )}
    </div>
  )
}
